import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import { create as modelsCreate } from 'pkg/api/models/actions';
import type {
	EventCarpool,
	EventCarpoolPassenger,
} from 'pkg/api/models/event_carpool';
import type { EventCarpoolRequest } from 'pkg/api/models/event_carpool_request';
import * as sdk from 'pkg/core/sdk';

export interface CarpoolPayload {
	seats: number;
	meetingPoint: string;
	note: string;
	departsAt?: number;
}

export const create = async (
	organizationId: number,
	eventId: number,
	payload: CarpoolPayload
): Promise<[boolean, EventCarpool]> => {
	const [request, response] = await modelsCreate<CarpoolPayload, EventCarpool>(
		endpoints.Events.AddCarpool(organizationId, eventId),
		payload
	);

	if (!request.ok) {
		return [false, null];
	}

	return [true, response];
};

export const update = async (
	organizationId: number,
	id: number,
	payload: CarpoolPayload
): Promise<[boolean, EventCarpool]> => {
	const request = await sdk.patch(
		endpoints.Events.UpdateCarpool(organizationId, id),
		{},
		payload
	);

	if (request.ok) {
		const resp = await request.json();

		return [true, resp];
	}

	return [false, null];
};

export const remove = async (
	organizationId: number,
	id: number
): Promise<boolean> => {
	const req = await sdk.destroy(
		endpoints.Events.DeleteCarpool(organizationId, id)
	);

	if (req.ok) {
		flashActions.show({
			title: t('Successfully removed car.'),
		});

		return true;
	}

	flashActions.show({
		title: t('Something went wrong.'),
	});

	return false;
};

export const join = async (
	organizationId: number,
	carpoolId: number,
	userId?: number
): Promise<[boolean, EventCarpoolPassenger]> => {
	const [request, response] = await modelsCreate<
		{ userId?: number },
		EventCarpoolPassenger
	>(endpoints.Events.AddCarpoolPassenger(organizationId, carpoolId), {
		userId,
	});

	if (!request.ok) {
		flashActions.show({
			title: t('Could not join the car.'),
		});

		return [false, null];
	}

	return [true, response];
};

export const leave = async (
	organizationId: number,
	carpoolId: number,
	userId?: number
): Promise<boolean> => {
	let url = endpoints.Events.DeleteCarpoolPassenger(organizationId, carpoolId);
	if (userId) {
		url += `?user_id=${userId}`;
	}

	const req = await sdk.destroy(url);

	// 404 means the passenger is already removed, treat it as done
	if (req.status === 404) {
		return true;
	}

	if (!req.ok) {
		flashActions.show({
			title: t('Something went wrong.'),
		});
	}

	return req.ok;
};

export const requestRide = async (
	organizationId: number,
	eventId: number,
	userId?: number
): Promise<[boolean, EventCarpoolRequest]> => {
	const [request, response] = await modelsCreate<
		{ userId?: number },
		EventCarpoolRequest
	>(endpoints.Events.AddCarpoolRequest(organizationId, eventId), { userId });

	if (!request.ok) {
		flashActions.show({
			title: t('Something went wrong.'),
		});

		return [false, null];
	}

	return [true, response];
};

export const withdrawRideRequest = async (
	organizationId: number,
	eventId: number,
	userId?: number
): Promise<boolean> => {
	let url = endpoints.Events.DeleteCarpoolRequest(organizationId, eventId);
	if (userId) {
		url += `?user_id=${userId}`;
	}

	const req = await sdk.destroy(url);

	// 404 means the request is already removed, e.g. fulfilled by joining a car
	if (req.status === 404) {
		return true;
	}

	if (!req.ok) {
		flashActions.show({
			title: t('Something went wrong.'),
		});
	}

	return req.ok;
};
